import { Box, Button, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import type { ActivityDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { formatDateTime } from '../../lib/format';
import { useCustomerContext } from './CustomerLayout';
import { useCustomerActivity } from './api';
import { Section, userLabel } from './parts';

const STEP = 10;

/** One event of the Activity tab: who, the action, where it came from, what happened, when. */
function ActivityCard({ a }: { a: ActivityDto }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, mb: 1 }}>
      <Typography sx={{ fontWeight: 600 }}>
        {userLabel(a.by) || 'system'}
        <Typography component="span" sx={{ ml: 1, fontWeight: 400 }}>
          {a.action}
        </Typography>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        From ({a.source})
      </Typography>
      <Typography sx={{ mt: 0.5 }}>{a.description}</Typography>
      <Typography variant="caption" color="text.secondary">
        On {formatDateTime(a.at)}
      </Typography>
    </Paper>
  );
}

/**
 * The "Activity" tab: a card per event, newest first, with "Load More..." that shows ten more.
 * (The reference loads further rows from the server the same way.)
 */
function ActivityTab({ customerId }: { customerId: string }) {
  const [size, setSize] = useState(STEP);
  const activity = useCustomerActivity(customerId, { page: 1, pageSize: size });
  const rows = activity.data?.data ?? [];
  const total = activity.data?.total ?? 0;
  return (
    <Box>
      <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
        Activities
      </Typography>
      {activity.isError ? (
        <Button onClick={() => void activity.refetch()}>Could not load. Try again</Button>
      ) : null}
      {!activity.isFetching && rows.length === 0 && !activity.isError ? (
        <Typography color="text.secondary">
          Nothing has been recorded for this customer yet.
        </Typography>
      ) : null}
      {rows.map((a) => (
        <ActivityCard key={a.id} a={a} />
      ))}
      {rows.length < total ? (
        <Button onClick={() => setSize((s) => s + STEP)} disabled={activity.isFetching}>
          Load More...
        </Button>
      ) : null}
    </Box>
  );
}

const COLUMNS: Column<ActivityDto>[] = [
  { key: 'by', header: 'Added By', render: (a) => userLabel(a.by) },
  { key: 'description', header: 'Description', render: (a) => a.description },
  { key: 'at', header: 'Date Time', nowrap: true, render: (a) => formatDateTime(a.at) },
  { key: 'action', header: 'Action', nowrap: true, render: (a) => a.action },
  { key: 'source', header: 'Source', render: (a) => a.source },
];

/**
 * The "Audit Log" tab: a table (Added By | Description | Date Time | Action | Source) with the
 * reference's "Search:" box, which narrows the rows as the user types (after a short pause).
 */
function AuditTab({ customerId }: { customerId: string }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(text.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [text]);
  const activity = useCustomerActivity(customerId, { page, pageSize, q });
  return (
    <Box>
      <TextField
        size="small"
        label="Search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 200 } }}
        sx={{ mb: 1.5 }}
      />
      <DataTable
        label="Audit Log"
        columns={COLUMNS}
        rows={activity.data?.data}
        rowKey={(a) => a.id}
        loading={activity.isFetching}
        error={activity.error}
        onRetry={() => void activity.refetch()}
        emptyText={q ? 'No matching entries.' : 'Nothing has been recorded for this customer yet.'}
        page={page}
        pageSize={pageSize}
        total={activity.data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        pageSizeOptions={[10, 20, 50, 100]}
      />
    </Box>
  );
}

/** Customer Activity Log (PC770): the tabs "Activity" and "Audit Log", newest first. */
export function ActivityPage() {
  const { customer } = useCustomerContext();
  const [tab, setTab] = useState<'activity' | 'audit'>('activity');
  return (
    <Section title="Customer Activity Log">
      <Tabs value={tab} onChange={(_e, v: 'activity' | 'audit') => setTab(v)} sx={{ mb: 1.5 }}>
        <Tab value="activity" label="Activity" />
        <Tab value="audit" label="Audit Log" />
      </Tabs>
      {tab === 'activity' ? (
        <ActivityTab customerId={customer.id} />
      ) : (
        <AuditTab customerId={customer.id} />
      )}
    </Section>
  );
}
